import { qs } from "@/lib/qs";
import { softApi } from "@/lib/soft-api";

export type YardStatus =
  | "lead"
  | "contacted"
  | "negotiation"
  | "pilot"
  | "customer"
  | "rejected"
  | "on_hold";

export type YardSource = "exhibition" | "inbound" | "cold" | "other";

export type ActivityType = "call" | "email" | "meeting" | "note";

export const YARD_STATUSES: YardStatus[] = [
  "lead",
  "contacted",
  "negotiation",
  "pilot",
  "customer",
  "rejected",
  "on_hold",
];

export const YARD_SOURCES: YardSource[] = ["exhibition", "inbound", "cold", "other"];

export const ACTIVITY_TYPES: ActivityType[] = ["call", "email", "meeting", "note"];

export const INTEREST_SUGGESTIONS = ["yard", "consortium", "soft", "asl", "other"];

/** Boat-yard product segments (stored as comma-separated `Yard.segment`). */
export const YARD_SEGMENTS = [
  "sail",
  "power",
  "rib",
  "inflatable",
  "aluminum",
  "composite",
  "commercial",
  "other",
] as const;

export type YardSegment = (typeof YARD_SEGMENTS)[number];

export function parseYardSegments(segment: string): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const part of segment.split(/[,;]/)) {
    const value = part.trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out;
}

export function formatYardSegments(values: readonly string[]): string {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const raw of values) {
    const value = raw.trim();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    out.push(value);
  }
  return out.join(",");
}

export type YardListItem = {
  id: string;
  name: string;
  country: string;
  city: string;
  status: YardStatus;
  interests: string[];
  source: YardSource;
  assigneeDisplayName: string;
  assigneeUserId: string;
  nextFollowUpAt: string | null;
  updatedAt: string;
  contactsCount?: number;
  activitiesCount?: number;
};

export type YardContact = {
  id: string;
  name: string;
  title: string;
  phone: string;
  email: string;
  isPrimary: boolean;
};

export type YardActivity = {
  id: string;
  type: ActivityType;
  body: string;
  happenedAt: string;
  createdByDisplayName: string;
};

export type InstanceStatus = "none" | "provisioning" | "running" | "stopped" | "error";

export type DeploymentMode = "saas" | "onprem";

/** Soft Deep: demo sandbox vs production box (ADR-0006 accessMode). */
export type AccessMode = "demo" | "production";

export type YardDetail = YardListItem & {
  website: string;
  phone: string;
  email: string;
  segment: string;
  notes: string;
  systemName: string | null;
  instanceStatus: InstanceStatus;
  instanceHost: string;
  instanceProfile: string;
  instanceDetail: string;
  instanceUpdatedAt: string | null;
  instanceAdminUsername: string;
  instanceAdminPasswordAvailable: boolean;
  instanceDesktopPairingTokenAvailable: boolean;
  deploymentMode: DeploymentMode;
  accessMode: AccessMode;
  lastTelemetryAt: string | null;
  contacts: YardContact[];
  activities: YardActivity[];
};

export type YardMetricSnapshot = {
  eventId: string;
  observedAt: string;
  podName: string;
  containerName: string;
  phase: string;
  ready: boolean;
  restartCount: number;
  cpuMilli: number | null;
  memoryBytes: number | null;
  networkRxBytes: number | null;
  networkTxBytes: number | null;
};

export type YardStorageSnapshot = {
  eventId: string;
  observedAt: string;
  pvcName: string;
  storageClass: string | null;
  phase: string;
  capacityBytes: number | null;
  usedBytes: number | null;
  availableBytes: number | null;
  probePodName: string | null;
  probeContainerName: string | null;
  mountPath: string | null;
};

export type YardLogEvent = {
  eventId: string;
  observedAt: string;
  podName: string;
  containerName: string;
  level: string;
  source: string;
  target: string | null;
  message: string;
};

export type YardObservabilityFacets = {
  pods?: string[];
  containers?: string[];
  pvcs?: string[];
  levels?: string[];
  levelCounts?: Record<string, number>;
};

export type AuthUser = {
  id: string;
  loginName: string;
  displayName: string;
};

export async function fetchMe(): Promise<AuthUser | null> {
  const data = await softApi.get<{ user: AuthUser | null }>("/api/auth/me");
  return data.user ?? null;
}

export async function login(loginName: string, password: string): Promise<boolean> {
  const res = await softApi.raw("/api/auth/login", {
    method: "POST",
    json: { login: loginName, password },
  });
  return res.ok;
}

export async function logout(): Promise<void> {
  await softApi.raw("/api/auth/logout", { method: "POST" });
}

export type NetworkPartnerStatus = "active" | "archived";

export type NetworkPartnerListItem = {
  id: string;
  displayName: string;
  notes: string;
  status: NetworkPartnerStatus;
  createdAt: string;
  updatedAt: string;
};

export type PartnerMember = {
  id: string;
  partnerId: string;
  loginName: string;
  displayName: string;
  role: string;
  idpUserId: string | null;
  status: "active" | "blocked";
  hasLogin: boolean;
  blocked: boolean;
  createdAt: string;
  updatedAt: string;
};

export type PartnerContract = {
  id: string;
  partnerId: string;
  contractLevel: string;
  status: string;
  validFrom: string;
  validTo: string | null;
  createdAt: string;
};

export type PartnerDetail = {
  partner: NetworkPartnerListItem;
  members: PartnerMember[];
  contracts: PartnerContract[];
};

export type YardListFilters = {
  q?: string;
  status?: string;
  source?: string;
  interest?: string;
};

export async function fetchYards(filters: YardListFilters = {}): Promise<YardListItem[]> {
  const q = filters.q?.trim();
  const interest = filters.interest?.trim();
  const data = await softApi.get<{ yards: YardListItem[] }>(
    `/api/yards${qs({
      ...(q && { q }),
      ...(filters.status && { status: filters.status }),
      ...(filters.source && { source: filters.source }),
      ...(interest && { interest }),
    })}`,
  );
  return data.yards;
}

export async function fetchYard(yardId: string): Promise<YardDetail> {
  const data = await softApi.get<{ yard: YardDetail }>(`/api/yards/${yardId}`);
  return data.yard;
}

export async function createYard(name: string): Promise<{ id: string }> {
  const data = await softApi.post<{ yard: { id: string } }>("/api/yards", { name });
  return data.yard;
}

export async function patchYardStatus(id: string, status: YardStatus): Promise<void> {
  await softApi.request(`/api/yards/${id}/status`, {
    method: "PATCH",
    json: { status },
    error: "status_failed",
    empty: true,
  });
}

export async function fetchPartners(q = ""): Promise<NetworkPartnerListItem[]> {
  const query = q.trim();
  const data = await softApi.get<{ partners: NetworkPartnerListItem[] }>(
    `/api/partners${qs({
      ...(query && { q: query }),
    })}`,
  );
  return data.partners;
}

export async function fetchPartner(partnerId: string): Promise<PartnerDetail> {
  return softApi.get<PartnerDetail>(`/api/partners/${partnerId}`);
}

export async function patchYard(
  yardId: string,
  patch: Record<string, unknown>,
): Promise<YardDetail> {
  const data = await softApi.patch<{ yard: YardDetail }>(`/api/yards/${yardId}`, patch);
  return data.yard;
}

export async function deleteYard(yardId: string): Promise<void> {
  await softApi.delete(`/api/yards/${yardId}`);
}

export type YardInstanceAction = "start" | "stop" | "purge";

export async function postYardInstance(
  yardId: string,
  body: {
    action: YardInstanceAction;
    systemName?: string;
  },
): Promise<void> {
  await softApi.request(`/api/yards/${yardId}/instance`, {
    method: "POST",
    json: body,
    error: "instance_failed",
    empty: true,
  });
}

export async function revealYardAdminPassword(yardId: string): Promise<string> {
  const data = await softApi.post<{ password?: string }>(
    `/api/yards/${yardId}/instance/admin-credentials`,
    undefined,
    "reveal_failed",
  );
  if (!data.password) throw new Error("reveal_failed");
  return data.password;
}

export async function revealYardDesktopPairingToken(
  yardId: string,
): Promise<string> {
  const data = await softApi.post<{ desktopPairingToken?: string }>(
    `/api/yards/${yardId}/instance/desktop-pairing`,
    undefined,
    "reveal_failed",
  );
  if (!data.desktopPairingToken) throw new Error("reveal_failed");
  return data.desktopPairingToken;
}

export type YardReleaseImage = {
  repository: string;
  tag: string;
};

export type YardReleaseGrant = {
  id: string;
  status: string;
  expiresAt: string;
  redeemedAt: string | null;
  createdAt: string;
};

export type YardRelease = {
  id: string;
  yardId: string;
  systemName: string;
  profile: string;
  images: YardReleaseImage[];
  digestFingerprint: string;
  chartRef: string;
  createdByDisplayName: string;
  createdAt: string;
  grant: YardReleaseGrant | null;
};

export type CreateYardReleaseResult = {
  release: YardRelease;
  installCode: string;
  installCodeRevealOnce: boolean;
  manifest: Record<string, unknown>;
};

export async function fetchYardReleases(yardId: string): Promise<YardRelease[]> {
  const data = await softApi.get<{ releases: YardRelease[] }>(
    `/api/yards/${yardId}/releases`,
  );
  return data.releases;
}

export async function createYardRelease(
  yardId: string,
  body: { profile?: string; imageTag?: string } = {},
): Promise<CreateYardReleaseResult> {
  return softApi.post<CreateYardReleaseResult>(
    `/api/yards/${yardId}/releases`,
    body,
    "release_failed",
  );
}

export async function reissueYardReleaseCode(
  yardId: string,
  releaseId: string,
): Promise<{ release: YardRelease; installCode: string }> {
  return softApi.post<{ release: YardRelease; installCode: string }>(
    `/api/yards/${yardId}/releases/${releaseId}/reissue`,
    undefined,
    "reissue_failed",
  );
}

export async function downloadYardReleaseManifest(
  yardId: string,
  releaseId: string,
): Promise<Record<string, unknown>> {
  return softApi.get<Record<string, unknown>>(
    `/api/yards/${yardId}/releases/${releaseId}/manifest`,
  );
}

export async function sendYardCredentialsEmail(
  yardId: string,
): Promise<{ to: string; contactName: string }> {
  const data = await softApi.post<{ to?: string; contactName?: string }>(
    `/api/yards/${yardId}/instance/send-credentials`,
    undefined,
    "send_credentials_failed",
  );
  if (!data.to) throw new Error("send_credentials_failed");
  return { to: data.to, contactName: data.contactName || "" };
}

export type YardMetricsQuery = {
  from?: string;
  to?: string;
  pod?: string;
  container?: string;
  limit?: number;
};

export type YardLogsQuery = {
  before?: string;
  after?: string;
  from?: string;
  to?: string;
  pod?: string;
  container?: string;
  level?: string;
  q?: string;
  limit?: number;
};

export async function fetchYardObservabilityMetrics(
  yardId: string,
  query: YardMetricsQuery = {},
): Promise<{ metrics: YardMetricSnapshot[]; storage: YardStorageSnapshot[]; facets: YardObservabilityFacets }> {
  return softApi.get(`/api/yards/${yardId}/observability/metrics${qs(query)}`);
}

export type YardLogsPage = {
  logs: YardLogEvent[];
  facets: YardObservabilityFacets;
  beforeCursor: string | null;
  afterCursor: string | null;
};

export async function fetchYardObservabilityLogs(
  yardId: string,
  query: YardLogsQuery = {},
): Promise<YardLogsPage> {
  return softApi.get(`/api/yards/${yardId}/observability/logs${qs(query)}`);
}

export type YardContactInput = {
  name: string;
  title: string;
  phone: string;
  email: string;
  isPrimary: boolean;
};

export async function createYardContact(
  yardId: string,
  contact: YardContactInput,
): Promise<void> {
  await softApi.request(`/api/yards/${yardId}/contacts`, {
    method: "POST",
    json: contact,
    error: "contact_failed",
    empty: true,
  });
}

export async function updateYardContact(
  contactId: string,
  contact: YardContactInput,
): Promise<void> {
  await softApi.request(`/api/contacts/${contactId}`, {
    method: "PATCH",
    json: contact,
    error: "contact_failed",
    empty: true,
  });
}

export async function deleteYardContact(contactId: string): Promise<void> {
  await softApi.delete(`/api/contacts/${contactId}`, "contact_delete_failed");
}

export async function createYardActivity(
  yardId: string,
  activity: { type: ActivityType; body: string },
): Promise<void> {
  await softApi.request(`/api/yards/${yardId}/activities`, {
    method: "POST",
    json: activity,
    error: "activity_failed",
    empty: true,
  });
}

export type CredentialDelivery = "show_in_ui" | "invite_email";

export type PartnerProvisionResult = {
  initialPassword?: string | null;
  inviteEmailSent?: boolean;
};

export async function createPartner(input: {
  displayName: string;
  notes: string;
}): Promise<{ id: string }> {
  const data = await softApi.post<{ partner: { id: string } }>("/api/partners", input);
  return data.partner;
}

export async function createPartnerMember(
  partnerId: string,
  input: {
    loginName: string;
    displayName: string;
    provisionLogin: boolean;
    credentialDelivery: CredentialDelivery;
  },
): Promise<PartnerProvisionResult> {
  return softApi.post<PartnerProvisionResult>(
    `/api/partners/${partnerId}/members`,
    input,
  );
}

export async function provisionPartnerMemberLogin(
  partnerId: string,
  memberId: string,
  input: { credentialDelivery: CredentialDelivery },
): Promise<PartnerProvisionResult> {
  return softApi.post<PartnerProvisionResult>(
    `/api/partners/${partnerId}/members/${memberId}/provision-login`,
    input,
    "provision_failed",
  );
}

/** Soft UI: «В архив» for a person — closes Forum login (Zitadel + Consortium). */
export async function archivePartnerMember(
  partnerId: string,
  memberId: string,
): Promise<void> {
  await softApi.post(
    `/api/partners/${partnerId}/members/${memberId}/block`,
    undefined,
    "member_block_failed",
  );
}

export async function restorePartnerMember(
  partnerId: string,
  memberId: string,
): Promise<void> {
  await softApi.post(
    `/api/partners/${partnerId}/members/${memberId}/unblock`,
    undefined,
    "member_unblock_failed",
  );
}

export async function deletePartner(partnerId: string): Promise<void> {
  await softApi.delete(`/api/partners/${partnerId}`, "partner_delete_failed");
}
